import { apiFetch, type QueryValue } from '@/lib/api'
import type { AdminService } from '@/services/contracts'
import { type SessionReadDto, toUserSession } from '@/services/http/auth'
import type {
  AuditLog,
  FirmBranch,
  ScopeDomain,
  FirmBranchOption,
  Group,
  McpConnection,
  Page,
  Permission,
  User,
  UserAccess,
} from '@/types/models'

/**
 * Backend gövdeleri sözleşmede (§5) yalnızca uç olarak tanımlı; alan adları burada
 * gerçek `UserRead`/`GroupRead`/vb. Pydantic modelleriyle birebir (entegrasyonda
 * `pnpm gen:api` çıktısından doğrulandı). İsim farkı olan yerler aşağıda uyarlanır:
 * `UserRead.groups` -> `User.group_ids`, `GroupRead.user_count` -> `Group.member_count`,
 * `AuditLogRead.created_at` -> `AuditLog.occurred_at`, `TemporaryPasswordResponse.user`
 * -> `.user_id`.
 */

interface UserGroupRefDto {
  id: string
  name: string
}

interface UserReadDto {
  id: string
  email: string
  full_name: string
  is_active: boolean
  is_superadmin: boolean
  must_change_password: boolean
  last_login_at: string | null
  locked_until: string | null
  created_at: string
  updated_at: string
  groups: UserGroupRefDto[]
}

interface UserAccessDto {
  user: UserReadDto
  is_locked: boolean
  failed_login_count: number
  permissions: string[]
  firm_scopes: Record<ScopeDomain, string[] | null>
}

interface RevokeResultDto {
  revoked: number
}

interface GroupReadDto {
  id: string
  name: string
  description: string | null
  permission_keys: string[]
  scopes: Record<ScopeDomain, { firms: string[]; branches: FirmBranch[] }>
  user_count: number
  version: number
  created_at: string
  updated_at: string
}

interface TemporaryPasswordDto {
  user: UserReadDto
  temporary_password: string
}

interface PermissionGroupDto {
  group: string
  permissions: Permission[]
}

interface PermissionCatalogDto {
  groups: PermissionGroupDto[]
}

interface AuditLogReadDto {
  id: string
  actor_id: string | null
  actor_email?: string | null
  actor_full_name?: string | null
  action: string
  target_type: string | null
  target_id: string | null
  meta: Record<string, unknown> | null
  ip: string | null
  user_agent: string | null
  created_at: string
}

function toUser(dto: UserReadDto): User {
  return {
    id: dto.id,
    email: dto.email,
    full_name: dto.full_name,
    is_active: dto.is_active,
    is_superadmin: dto.is_superadmin,
    group_ids: dto.groups.map((group) => group.id),
    created_at: dto.created_at,
    last_login_at: dto.last_login_at,
  }
}

function toUserAccess(dto: UserAccessDto): UserAccess {
  return {
    user: toUser(dto.user),
    groups: dto.user.groups,
    must_change_password: dto.user.must_change_password,
    is_locked: dto.is_locked,
    locked_until: dto.user.locked_until,
    failed_login_count: dto.failed_login_count,
    permissions: dto.permissions,
    firm_scopes: dto.firm_scopes,
  }
}

function toGroup(dto: GroupReadDto): Group {
  return {
    id: dto.id,
    name: dto.name,
    description: dto.description,
    permission_keys: dto.permission_keys,
    scopes: dto.scopes,
    member_count: dto.user_count,
    version: dto.version,
  }
}

function toAuditLog(dto: AuditLogReadDto): AuditLog {
  return {
    id: dto.id,
    occurred_at: dto.created_at,
    actor_id: dto.actor_id,
    actor_email: dto.actor_email ?? null,
    actor_full_name: dto.actor_full_name ?? null,
    action: dto.action,
    target_type: dto.target_type,
    target_id: dto.target_id,
    meta: dto.meta,
    ip: dto.ip,
    user_agent: dto.user_agent,
  }
}

/**
 * `GET /groups` sayfalıdır ama admin ekranında sayfalama kontrolü yok; katalog
 * küçük kabul edilip tek sayfada, sözleşme §3'ün üst sınırıyla (`page_size<=200`)
 * çekiliyor. 200'den fazla grup olursa bu varsayım `docs/OPEN-QUESTIONS.md`'ye düşer.
 */
const GROUPS_PAGE_SIZE = 200

export const httpAdminService: AdminService = {
  async listUsers(params) {
    const page = await apiFetch<Page<UserReadDto>>('/users', {
      query: params as unknown as Record<string, QueryValue>,
    })
    return { ...page, items: page.items.map(toUser) }
  },

  async getUser(userId) {
    return toUser(await apiFetch<UserReadDto>(`/users/${userId}`))
  },

  async getUserAccess(userId) {
    return toUserAccess(await apiFetch<UserAccessDto>(`/users/${userId}/access`))
  },

  async listUserSessions(userId) {
    // `created_at` = ailenin son yenilemesi (refresh token dönünce yeni satır yazılır).
    return (await apiFetch<SessionReadDto[]>(`/users/${userId}/sessions`)).map(toUserSession)
  },

  async revokeUserSessions(userId, sessionId) {
    const path = sessionId ? `/users/${userId}/sessions/${sessionId}` : `/users/${userId}/sessions`
    return (await apiFetch<RevokeResultDto>(path, { method: 'DELETE' })).revoked
  },

  async listUserMcpConnections(userId) {
    return apiFetch<McpConnection[]>(`/users/${userId}/mcp-connections`)
  },

  async revokeUserMcpConnections(userId, connectionId) {
    const path = connectionId
      ? `/users/${userId}/mcp-connections/${connectionId}`
      : `/users/${userId}/mcp-connections`
    return (await apiFetch<RevokeResultDto>(path, { method: 'DELETE' })).revoked
  },

  async unlockUser(userId) {
    return toUserAccess(
      await apiFetch<UserAccessDto>(`/users/${userId}/unlock`, { method: 'POST' }),
    )
  },

  async createUser(request) {
    const dto = await apiFetch<TemporaryPasswordDto>('/users', { method: 'POST', body: request })
    return { user_id: dto.user.id, temporary_password: dto.temporary_password }
  },

  async updateUser(userId, request) {
    return toUser(
      await apiFetch<UserReadDto>(`/users/${userId}`, { method: 'PATCH', body: request }),
    )
  },

  async resetUserPassword(userId) {
    const dto = await apiFetch<TemporaryPasswordDto>(`/users/${userId}/reset-password`, {
      method: 'POST',
    })
    return { user_id: dto.user.id, temporary_password: dto.temporary_password }
  },

  async listGroups() {
    const page = await apiFetch<Page<GroupReadDto>>('/groups', {
      query: { page: 1, page_size: GROUPS_PAGE_SIZE },
    })
    return page.items.map(toGroup)
  },

  async getGroup(groupId) {
    return toGroup(await apiFetch<GroupReadDto>(`/groups/${groupId}`))
  },

  async createGroup(request) {
    return toGroup(await apiFetch<GroupReadDto>('/groups', { method: 'POST', body: request }))
  },

  async updateGroup(groupId, request) {
    return toGroup(
      await apiFetch<GroupReadDto>(`/groups/${groupId}`, { method: 'PATCH', body: request }),
    )
  },

  async deleteGroup(groupId) {
    await apiFetch<void>(`/groups/${groupId}`, { method: 'DELETE' })
  },

  async setGroupPermissions(groupId, permissionKeys, version) {
    return toGroup(
      await apiFetch<GroupReadDto>(`/groups/${groupId}/permissions`, {
        method: 'PUT',
        body: { permission_keys: permissionKeys, version },
      }),
    )
  },

  async setGroupScope(groupId, domain, firms, branches, version) {
    return toGroup(
      await apiFetch<GroupReadDto>(`/groups/${groupId}/scopes/${domain}`, {
        method: 'PUT',
        body: { firms, branches, version },
      }),
    )
  },

  async listFirmBranchOptions(domain) {
    return apiFetch<FirmBranchOption[]>('/permissions/firm-branches', { query: { domain } })
  },

  async listPermissions() {
    const catalog = await apiFetch<PermissionCatalogDto>('/permissions')
    return catalog.groups.flatMap((group) => group.permissions)
  },

  async listAuditLogs(query) {
    const page = await apiFetch<Page<AuditLogReadDto>>('/audit-logs', {
      query: query as unknown as Record<string, QueryValue>,
    })
    return { ...page, items: page.items.map(toAuditLog) }
  },
}
